import {
  DELETE_DOMAIN,
  type DeleteDomainData,
  type DomainVariables,
} from "../CustomerProvisioningApi";
import React, { useContext } from "react";
import { showToast } from "../../Forms/Dialogs/Toast/Toast";
import AddDomain from "../AddDomain/AddDomain";
import { createColumnHelper } from "@tanstack/react-table";
import "./AllowedDomainsModal.scss";
import {
  TextButton,
  IconButton,
  Table,
  TableCell,
  tableCellTypesEnum,
  useMutationWrapper,
  ModalContext,
  ModalButtonsContainer,
  showDialog,
  showErrorDialog,
} from "@SeemplicityDev/shared";

interface AllowedDomainsModalProps {
  organizationSlug: string;
  domains: string[];
}

const AllowedDomainsModal: React.FC<AllowedDomainsModalProps> = ({
  organizationSlug,
  domains,
}) => {
  const { openModal, closeModal } = useContext(ModalContext);
  const [deleteDomain] = useMutationWrapper<DeleteDomainData, DomainVariables>(
    DELETE_DOMAIN
  );

  const columnHelper = createColumnHelper<string>();

  const columns = [
    columnHelper.accessor((domain) => domain, {
      id: "domain",
      cell: (info) => <TableCell>{info.getValue()}</TableCell>,
      header: "Domain",
      enableColumnFilter: false,
      size: 232,
      maxSize: 232,
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      enableColumnFilter: false,
      cell: (cellProps) => {
        const domainName = cellProps.row.getValue<string>("domain");

        return domains.length > 1 ? (
          <TableCell
            type={tableCellTypesEnum.COMPONENT}
            className={"actions-cell"}
          >
            <IconButton
              data-testid={`delete-domain-${domainName}`}
              buttonStyle={"danger"}
              withTooltip={{ content: "Delete Domain" }}
              isIcon="seem-icon-bin"
              type={`button`}
              onClick={() =>
                showDialog({
                  title: "Delete Domain",
                  message: (
                    <div className={"modal-text"}>
                      Delete the domain
                      <span className={"modal-live-text"}> {domainName}</span>?
                    </div>
                  ),
                  buttons: [
                    {
                      "data-testid": "cancel-delete",
                      label: "Cancel",
                    },
                    {
                      "data-testid": "confirm-delete",
                      buttonStyle: "danger",
                      label: "Delete Domain",
                      type: "submit",
                      onClick: () => handleDeletion(domainName),
                    },
                  ],
                })
              }
            />
          </TableCell>
        ) : (
          <></>
        );
      },
    }),
  ];

  const handleDeletion = (domainToDelete: string) => {
    deleteDomain({
      variables: {
        domain: domainToDelete,
        organization_slug: organizationSlug,
      },
      refetchQueries: ["cnc_organizations"],
    })
      .then((res) => {
        showToast({ message: res.data?.cnc_delete_domain?.msg });
        closeModal();
      })
      .catch((error: Error) => {
        showErrorDialog({ message: error.message });
      });
  };

  return (
    <div className={"allowed-domains-wrap"}>
      <TextButton
        buttonStyle={"primary"}
        wrapperClassName={"add-domain-btn"}
        label={"+ Add Domain"}
        data-testid={"add-domain"}
        onClick={() =>
          openModal({
            title: "Add Domain",
            size: "xs",
            overlay: true,
            component: <AddDomain organizationSlug={organizationSlug} />,
          })
        }
        title={"Add"}
      />
      <div className={"allowed-domains-scroll"}>
        <Table
          className={"add-domain-table"}
          columns={columns}
          data={domains}
          data-testid={"domains-list"}
        />
      </div>

      <ModalButtonsContainer centerButtons>
        <TextButton
          type="button"
          data-testid={"allowed-domains-close"}
          label={"Close"}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            closeModal();
          }}
        />
      </ModalButtonsContainer>
    </div>
  );
};
export default AllowedDomainsModal;
